import {ReactNode, useEffect, useRef, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router';
import {Trans} from '@ui/i18n/trans';
import {apiClient} from '@common/http/query-client';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {ImageZoomDialog} from '@ui/overlays/dialog/image-zoom-dialog';
import type {BlockInstance} from '../appearance-data';
import {getAppearanceContent} from '../appearance-data';
import {EyebrowPill} from './eyebrow-pill';
import {AmbientOrbs, Reveal} from '../motion';
import {Carousel} from '../carousel';
import {accent} from './accents';
import {Section, SectionHeading} from './section-kit';

type P = {block: BlockInstance; props: Record<string, any>};

/**
 * Counts up to a target number when the element scrolls into view. Preserves any
 * non-numeric prefix/suffix (e.g. "10,000+", "$2M", "95%").
 */
function CountUp({value, animate}: {value: string; animate: boolean}) {
  const ref = useRef<HTMLSpanElement>(null);
  const match = String(value ?? '').match(/^(\D*)([\d,.]+)(\D*)$/);
  const target = match ? parseFloat(match[2].replace(/,/g, '')) : NaN;
  const [display, setDisplay] = useState(animate && !isNaN(target) ? `${match![1]}0${match![3]}` : value);

  useEffect(() => {
    if (!animate || isNaN(target) || !ref.current) return;
    const el = ref.current;
    let raf = 0;
    const io = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const dur = 1400;
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        const n = Math.round(target * eased);
        setDisplay(`${match![1]}${n.toLocaleString()}${match![3]}`);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, {threshold: 0.3});
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [animate, target]);

  return <span ref={ref}>{display}</span>;
}

export function BannerBlock({props}: P) {
  return (
    <section className="relative isolate overflow-hidden px-24 py-64 text-center"
      style={{color: props.text_color || '#fff'}}>
      {props.image ? (
        <img src={props.image} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
      ) : null}
      <div className="absolute inset-0 -z-10" style={{backgroundColor: props.overlay || '#000', opacity: 0.45}} />
      <div className="mx-auto max-w-[820px] space-y-12">
        <EyebrowPill text={props.eyebrow} />
        {props.heading ? <h2 className="text-3xl font-bold">{props.heading}</h2> : null}
        {props.text ? <p className="opacity-90">{props.text}</p> : null}
        {props.button_label ? (
          <Button variant="flat" color="primary" className="mt-8 rounded-full"
            elementType={Link} to={props.button_url || '/'}>{props.button_label}</Button>
        ) : null}
      </div>
    </section>
  );
}

export function ImageBlock({props}: P) {
  const width = props.width === 'narrow' ? 'max-w-720' : props.width === 'full' ? 'max-w-none' : 'max-w-[1100px]';
  if (!props.image) return null;
  const img = <img src={props.image} alt={props.alt || ''} className="mx-auto w-full rounded-2xl object-cover" />;
  return (
    <section className="px-24 py-40">
      <div className={`mx-auto ${width}`}>
        {props.link ? <Link to={props.link}>{img}</Link> : img}
      </div>
    </section>
  );
}

export function NewsletterBlock({props}: P) {
  const bg = props.bg_color || '#4f46e5';
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === 'loading') return;
    setStatus('loading');
    try {
      await apiClient.post('appearance/subscribe', {email: email.trim(), source: 'newsletter-block'});
      setStatus('done');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="px-24 py-48"
      style={{background: `linear-gradient(120deg, ${bg} 0%, #7c3aed 100%)`, color: props.text_color || '#fff'}}>
      {/* Heading/text on the left, inline form on the right (stacks on mobile). */}
      <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-20 text-center md:flex-row md:justify-between md:text-start">
        <div>
          <EyebrowPill text={props.eyebrow} />
          {props.heading ? <h2 className="text-2xl font-bold">{props.heading}</h2> : null}
          {props.text ? <p className="mt-1 opacity-90">{props.text}</p> : null}
        </div>
        {status === 'done' ? (
          <p className="flex items-center gap-8 rounded-full bg-white/15 px-20 py-12 text-sm font-medium">
            <span className="material-icons text-base">check_circle</span>
            <Trans message="Thanks! You're subscribed." />
          </p>
        ) : (
          <form className="flex w-full max-w-[480px] flex-col gap-4" onSubmit={submit}>
            <div className="flex gap-8">
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-44 flex-1 rounded-full border-0 px-16 text-base text-black sm:text-sm" />
              <Button type="submit" variant="flat" color="primary" disabled={status === 'loading'}
                className="rounded-full bg-white !text-primary">
                {status === 'loading' ? <Trans message="…" /> : (props.button_label || 'Subscribe')}
              </Button>
            </div>
            {status === 'error' ? (
              <span className="ps-16 text-start text-xs opacity-90"><Trans message="Something went wrong. Please try again." /></span>
            ) : null}
          </form>
        )}
      </div>
    </section>
  );
}

/** Star rating (1–5). Filled up to `rating`, the rest faded. Defaults to 5. */
function Stars({rating}: {rating?: number | string}) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
  if (!n) return null;
  return (
    <span className="flex shrink-0 gap-1" aria-label={`${n} / 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} viewBox="0 0 24 24" className={`h-14 w-14 ${i <= n ? 'text-warning' : 'text-warning/25'}`} fill="currentColor">
          <path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6z" />
        </svg>
      ))}
    </span>
  );
}

export function TestimonialsBlock({props}: P) {
  // Source = the Testimonials content system (default) or manual repeater items.
  const items: any[] = props.source === 'manual'
    ? (Array.isArray(props.items) ? props.items : [])
    : getAppearanceContent().testimonials;
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <Cards layout={props.layout} heading={props.heading}>
          {items.map((t, i) => {
            const initials = String(t.name || '?').trim().split(/\s+/).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
            return (
              <Reveal key={i} index={i} className="h-full">
              <figure className="ap-lift relative flex h-full flex-col rounded-2xl border border-divider bg-paper p-20 shadow-sm transition hover:border-primary/40 hover:shadow-lg">
                {/* Oversized quote mark — a cheap, classic way to make a wall of
                    review cards feel designed rather than boxed. */}
                <span aria-hidden
                  className="pointer-events-none absolute right-14 top-6 select-none font-serif text-5xl leading-none text-primary/10">
                  &rdquo;
                </span>
                {/* Content is a photo, a link, or the rich-HTML quote. */}
                {t.quote_type === 'photo' && t.quote_image ? (
                  // No flex-1/fixed height here — the card's equal-height grid would
                  // clip a tall screenshot. Let the image size to its own aspect ratio
                  // so the WHOLE photo is always visible.
                  <a href={t.quote_url || undefined} target={t.quote_url ? '_blank' : undefined} rel="noopener noreferrer"
                    className="mb-14 block">
                    <img src={t.quote_image} alt={t.name || ''} className="block h-auto w-full rounded-lg" loading="lazy"
                      // A hot-linked photo (a Facebook CDN link, say) expires; hide the
                      // broken frame rather than show an empty box on the homepage.
                      onError={e => { const a = e.currentTarget.closest('a'); if (a) a.hidden = true; }} />
                  </a>
                ) : t.quote_type === 'url' && t.quote_url ? (
                  <a href={t.quote_url} target="_blank" rel="noopener noreferrer"
                    className="mb-14 flex flex-1 items-center gap-6 text-sm font-medium text-primary hover:underline">
                    <span className="material-icons text-base">open_in_new</span>
                    <Trans message="Read the full review" />
                  </a>
                ) : t.quote ? (
                  // Rich HTML (sanitized server-side for the Testimonials system).
                  <blockquote
                    className="mb-14 flex-1 text-sm leading-relaxed text-main [&_a]:text-primary [&_a]:underline [&_img]:rounded-lg [&_li]:ms-16 [&_ol]:list-decimal [&_ul]:list-disc"
                    dangerouslySetInnerHTML={{__html: t.quote}}
                  />
                ) : null}
                <figcaption className="flex items-center gap-10 border-t border-divider pt-12">
                  {t.avatar ? (
                    <img src={t.avatar} alt="" className="h-40 w-40 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-40 w-40 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{initials}</span>
                  )}
                  <div className="min-w-0">
                    {/* Stars sit right after the name, and follow the item's rating. */}
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                      {t.name ? <p className="text-sm font-semibold">{t.name}</p> : null}
                      <Stars rating={t.rating} />
                    </div>
                    {t.role ? <p className="text-xs text-muted">{t.role}</p> : null}
                  </div>
                </figcaption>
              </figure>
              </Reveal>
            );
          })}
        </Cards>
      </div>
    </Section>
  );
}

/**
 * Renders testimonial cards either as the default responsive grid or, when the
 * block's `layout` is `carousel`, as a swipeable rail. Keeps the card markup
 * itself in one place.
 */
function Cards({
  layout,
  heading,
  children,
}: {
  layout?: string;
  heading?: string;
  children: ReactNode[];
}) {
  if (layout === 'carousel') {
    // Same exact-fit sizing as the featured carousel — fixed-width cards
    // overflowed the arrow-gutter track and clipped the last one.
    return (
      <Carousel
        itemClass="w-[280px] sm:w-[320px] md:w-[calc((100%-40px)/3)]"
        ariaLabel={heading}
      >
        {children}
      </Carousel>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-24 md:grid-cols-2 lg:grid-cols-4">
      {children}
    </div>
  );
}

export function FaqBlock({props}: P) {
  // Source = the FAQ content system (managed centrally) or manual repeater items.
  const items: any[] = props.source === 'system'
    ? getAppearanceContent().faqs
    : (Array.isArray(props.items) ? props.items : []);
  if (!items.length) return null;
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[820px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2 md:gap-x-24">
          {items.map((q, i) => (
            <Reveal key={i} index={i}>
            <details className="group/faq rounded-xl border border-divider bg-paper p-16 transition hover:border-primary/40 open:border-primary/40 open:shadow-md">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-10 text-sm font-semibold [&::-webkit-details-marker]:hidden">
                {q.question}
                {/* Round +/× affordance instead of a bare glyph. */}
                <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base leading-none text-primary transition group-open/faq:rotate-45">
                  ＋
                </span>
              </summary>
              {q.answer ? (
                // Answer is rich HTML (sanitized server-side for the FAQ system;
                // manual answers pass through the same builder rich-text field).
                <div className="prose-faq mt-8 text-sm leading-relaxed text-muted [&_a]:text-primary [&_a]:underline [&_img]:rounded-lg [&_li]:ms-16 [&_ol]:list-decimal [&_ul]:list-disc"
                  dangerouslySetInnerHTML={{__html: q.answer}} />
              ) : null}
            </details>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

export function GalleryBlock({props}: P) {
  const galleries = getAppearanceContent().galleries;
  const gallery = props.gallery_slug
    ? galleries.find(g => g.slug === props.gallery_slug)
    : galleries[0];
  const items = gallery?.items ?? [];
  // Which image is open in the lightbox (null = closed). Declared before the
  // early return so hook order stays stable.
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const cols = props.columns === '4' ? 'md:grid-cols-4' : props.columns === '2' ? 'md:grid-cols-2' : 'md:grid-cols-3';
  if (!items.length) return null;

  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-2 gap-12 ${cols}`}>
          {items.map((item, i) => (
            <figure key={item.id}>
              {/* Click opens the full-size lightbox (close / prev / next inside). */}
              <button type="button" onClick={() => setZoomIndex(i)}
                aria-label={item.caption || 'View image'}
                className="group block w-full cursor-zoom-in overflow-hidden rounded-xl">
                <img src={item.image} alt={item.caption || ''}
                  className="aspect-square w-full object-cover transition duration-300 group-hover:scale-105"
                  loading="lazy" />
              </button>
              {item.caption ? (
                <figcaption className="mt-4 text-xs text-muted">
                  {/* Keep the item's link reachable now that the image zooms. */}
                  {item.link ? (
                    <Link to={item.link} className="text-primary hover:underline">{item.caption}</Link>
                  ) : (
                    item.caption
                  )}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      </div>

      {/* Fullscreen viewer — reuses the framework's zoom dialog. */}
      <DialogTrigger
        type="modal"
        isOpen={zoomIndex !== null}
        onOpenChange={isOpen => {
          if (!isOpen) setZoomIndex(null);
        }}
      >
        <ImageZoomDialog
          images={items.map(it => it.image)}
          activeIndex={zoomIndex ?? 0}
          onActiveIndexChange={setZoomIndex}
        />
      </DialogTrigger>
    </Section>
  );
}

export function StatsBlock({props}: P) {
  const items: any[] = Array.isArray(props.items) ? props.items : [];
  const animate = props.animate !== false;
  // 2 per row on phones, 3 on tablets, then fan out on desktop by the item count
  // so nothing wraps awkwardly. Every cell is centered → the grid always lines up.
  const colsLg = items.length === 5 ? 'lg:grid-cols-5' : items.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
  // Dark band by default. The homepage is otherwise a long run of white
  // card grids — one deep section breaks that rhythm and makes the headline
  // numbers land instead of blending in. An explicit bg_color still wins.
  const custom = !!props.bg_color;
  return (
    <section
      className="relative isolate overflow-hidden px-24 py-64"
      style={
        custom
          ? {backgroundColor: props.bg_color}
          : {background: 'linear-gradient(135deg, #131a35 0%, #221a4d 55%, #2c1a52 100%)'}
      }
    >
      {!custom && <AmbientOrbs colors={['#5038ED', '#7C3AED']} />}
      <div className={`mx-auto max-w-[1100px] ${custom ? '' : 'text-white'}`}>
        <EyebrowPill text={props.eyebrow} />
        {props.heading ? (
          <h2 className="mb-40 text-center text-2xl font-bold md:text-3xl">{props.heading}</h2>
        ) : null}
        <div className={`grid grid-cols-2 gap-x-16 gap-y-36 sm:grid-cols-3 ${colsLg}`}>
          {items.map((s, i) => (
            <Reveal key={i} index={i}>
              <div className="flex flex-col items-center gap-8 text-center">
                {s.image ? (
                  <img src={s.image} alt="" className="h-52 w-52 shrink-0 rounded-2xl object-contain" />
                ) : s.icon ? (
                  <span
                    className={`ap-float ap-delay-${(i % 3) + 1} flex h-52 w-52 shrink-0 items-center justify-center rounded-2xl ${
                      custom ? 'bg-primary/10 text-primary' : 'bg-white/10 text-white ring-1 ring-white/15 backdrop-blur'
                    }`}
                  >
                    <span className="material-icons text-2xl">{s.icon}</span>
                  </span>
                ) : null}
                <p
                  className={`text-3xl font-extrabold leading-none md:text-[40px] ${
                    custom ? 'text-primary' : 'text-white'
                  }`}
                >
                  <CountUp value={s.value} animate={animate} />
                </p>
                <p className={`text-sm ${custom ? 'text-muted' : 'text-white/70'}`}>{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
